import { HttpClient } from '@angular/common/http';
import { inject, Injectable, isDevMode } from '@angular/core';
import { PrismicDocument, RTNode } from '@prismicio/client';
import { ProductCatalogService } from '@yol-digital/ms-client';
import { validatePromotionCode } from '@yol-digital/yol-regex';
import { lastValueFrom, map, Observable } from 'rxjs';
import { ENVIRONMENT_URLS_CONFIG_TOKEN } from 'app-initialization';
import { FeatureFlagService } from 'feature-flag';
import { Channel, CmsService, PromotionDocument } from 'prismic';
import { ObservableCachedDataLoaderService } from 'utils';
import { Promotion } from './promotion.class';
import Api = ProductCatalogService.Api;
export type PromotionResponse = ProductCatalogService.PromotionResponse;

export interface RawPromotion {
  id: string;
  code: string;
  lockin_lengths: { lockin_length: string }[];
  discount_amount: number;
  discount_duration: number;
  priority: number;
  title: [RTNode, ...RTNode[]];
  available_in_channels: { channel: Channel; availability: 'Default' | 'Non default' }[];
  rfe_promotion_code: string;
  zira_promotion_code: string;
}

@Injectable({
  providedIn: 'root',
})
export abstract class PromotionService {
  protected api: Api;
  protected config = inject(ENVIRONMENT_URLS_CONFIG_TOKEN);
  protected http = inject(HttpClient);
  private cms = inject(CmsService);
  private featureFlagService = inject(FeatureFlagService);
  protected observableDataLoader = inject(ObservableCachedDataLoaderService);
  public defaultChannel = 'ol';

  constructor() {
    this.api = new Api(this.config.newMicroServiceEndpoint, this.http);
  }

  protected abstract getPromotionByCode(
    code: string
  ): Observable<{ promotions: PromotionResponse[] } | { error: string }>;

  public abstract getPCPromotionByCode(
    code: string
  ): Observable<{ promotions: PromotionResponse[] } | { error: string }>;

  public normalizeAndValidatePromotionCode(code: string | null | undefined): string | null {
    if (!code) return null;
    const trimmedCode = code.trim();
    if (!trimmedCode) return null;
    const validationErrors = validatePromotionCode(trimmedCode);
    return validationErrors ? null : trimmedCode;
  }

  public async getPromotionFromPrismicByCode(code: string, productSpecClass: string) {
    if (!code) return null;
    const isZiraEnabled = this.isZiraEnabledForClass(productSpecClass);

    const promotionCodeField = isZiraEnabled ? 'zira_promotion_code' : 'rfe_promotion_code';
    const resp = await this.cms.getByContentRelationship('promotion', promotionCodeField, code);

    return await this.formatAndValidatePromotion(resp[0], productSpecClass);
  }

  private isZiraEnabledForClass(productSpecClass: string): boolean {
    const ziraEnabledProductClasses = this.featureFlagService
      .getFeatureValue('zira-enabled-product-classes', '')
      .split(',');
    return ziraEnabledProductClasses.includes(productSpecClass);
  }

  public async getPromotionFromPrismicById(id: string, productSpecClass: string) {
    const prismicPromotion = await this.cms.getById(id);

    return await this.formatAndValidatePromotion(prismicPromotion as PromotionDocument, productSpecClass);
  }

  private formatAndValidatePromotion(
    doc: PrismicDocument | PromotionDocument,
    productSpecClass: string
  ): Promise<Promotion> {
    if (!doc) return;
    if ((doc as PrismicDocument).data.environment === 'Test' && !isDevMode()) return;
    const isZiraEnabled = this.isZiraEnabledForClass(productSpecClass);
    const data = doc.data as unknown as RawPromotion;
    const promoCode = isZiraEnabled ? data.zira_promotion_code : data.rfe_promotion_code;
    return lastValueFrom(
      this.getPCPromotionByCode(promoCode).pipe(
        map((response: { promotions: PromotionResponse[] }) => {
          const msPromotion = response.promotions[0];
          const promo = new Promotion(msPromotion, doc as PromotionDocument);
          promo.code = isZiraEnabled ? data.zira_promotion_code : data.rfe_promotion_code;
          if (!promo.code) return null;
          return promo;
        })
      )
    );
  }
}
